import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { PackageCheck, Paperclip, X } from 'lucide-react';
import { FormSheet } from '@/components/common/FormSheet';
import { FormField } from '@/components/common/FormField';
import { DialogEyebrow, SectionHeading } from '@/components/common/DialogPrimitives';
import { DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  procurementService,
  type CreateReceiptPayload,
} from '@/services/procurement';
import type { ProcurementDocKind, PurchaseRequest } from '@/types/procurement';
import { fmt } from '@/lib/format';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  request: PurchaseRequest;
}

interface ReceiptLineState {
  itemId: string;
  purchaseRequestItemId: string;
  sku: string;
  name: string;
  remaining: number;
  qty: string;
}

const DOC_SLOTS: Array<{ kind: ProcurementDocKind; label: string }> = [
  { kind: 'INVOICE', label: 'Invoice' },
  { kind: 'CHALLAN', label: 'Challan' },
  { kind: 'EWAY_BILL', label: 'E-way bill' },
];

const ACCEPT = 'application/pdf,image/png,image/jpeg';

export function ReceiptFormSheet({ open, onOpenChange, request }: Props) {
  const qc = useQueryClient();
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [invoiceDate, setInvoiceDate] = useState('');
  const [challanNumber, setChallanNumber] = useState('');
  const [ewayBillNumber, setEwayBillNumber] = useState('');
  const [actualTotal, setActualTotal] = useState('');
  const [notes, setNotes] = useState('');
  const [files, setFiles] = useState<Partial<Record<ProcurementDocKind, File>>>({});
  const [lines, setLines] = useState<ReceiptLineState[]>([]);

  useEffect(() => {
    if (!open) return;
    setInvoiceNumber('');
    setInvoiceDate('');
    setChallanNumber('');
    setEwayBillNumber('');
    setActualTotal('');
    setNotes('');
    setFiles({});
    setLines(
      request.items.map((l) => {
        const remaining = Math.max(0, Number(l.quantity) - Number(l.receivedQuantity));
        return {
          itemId: l.itemId,
          purchaseRequestItemId: l.id,
          sku: l.sku,
          name: l.name,
          remaining,
          qty: remaining > 0 ? String(remaining) : '',
        };
      }),
    );
  }, [open, request]);

  const validLines = useMemo(
    () => lines.filter((l) => Number(l.qty) > 0),
    [lines],
  );

  const mutation = useMutation({
    mutationFn: async () => {
      if (validLines.length === 0) {
        throw new Error('Enter a received quantity on at least one line.');
      }

      // Upload any attached documents first, then link them on the receipt.
      const attachmentIds: string[] = [];
      for (const slot of DOC_SLOTS) {
        const file = files[slot.kind];
        if (!file) continue;
        const att = await procurementService.uploadAttachment({ file, docKind: slot.kind });
        attachmentIds.push(att.id);
      }

      const payload: CreateReceiptPayload = {
        purchaseRequestId: request.id,
        lines: validLines.map((l) => ({
          itemId: l.itemId,
          quantityReceived: Number(l.qty),
          purchaseRequestItemId: l.purchaseRequestItemId,
        })),
        supplierInvoiceNumber: invoiceNumber.trim() || undefined,
        invoiceDate: invoiceDate ? new Date(invoiceDate).toISOString() : undefined,
        challanNumber: challanNumber.trim() || undefined,
        ewayBillNumber: ewayBillNumber.trim() || undefined,
        actualTotal: actualTotal ? Number(actualTotal) : undefined,
        notes: notes.trim() || undefined,
        attachmentIds: attachmentIds.length ? attachmentIds : undefined,
      };
      return procurementService.createReceipt(payload);
    },
    onSuccess: (receipt) => {
      toast.success(`Goods received — ${receipt.grnNumber}`);
      qc.invalidateQueries({ queryKey: ['purchase-requests'] });
      qc.invalidateQueries({ queryKey: ['purchase-request', request.id] });
      onOpenChange(false);
    },
    onError: (err: unknown) =>
      toast.error(err instanceof Error ? err.message : 'Could not record receipt'),
  });

  const onSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    mutation.mutate();
  };

  return (
    <FormSheet
      open={open}
      onOpenChange={onOpenChange}
      headerSlot={
        <div className="flex items-start gap-4">
          <span
            aria-hidden="true"
            className="grid h-14 w-14 shrink-0 place-items-center rounded-lg bg-gradient-to-br from-primary/10 to-primary/5 text-primary ring-1 ring-inset ring-primary/15"
          >
            <PackageCheck className="h-6 w-6" />
          </span>
          <div className="min-w-0 flex-1 space-y-1.5">
            <DialogEyebrow>Goods receipt · {request.prNumber}</DialogEyebrow>
            <DialogTitle className="text-[18px] font-semibold leading-tight tracking-tight">
              Inward received materials
            </DialogTitle>
            <DialogDescription className="text-[12.5px] leading-relaxed">
              Confirm received quantities and attach the invoice, challan and e-way
              bill. This adds the stock and updates the request.
            </DialogDescription>
          </div>
        </div>
      }
      title=""
      submitLabel="Receive + add stock"
      onSubmit={onSubmit}
      isSubmitting={mutation.isPending}
      error={mutation.error}
      size="4xl"
      fixedHeight="lg"
    >
      <div className="space-y-6">
        <section className="space-y-3">
          <SectionHeading title="Received quantities" />
          <div className="space-y-1.5">
            {lines.map((l, idx) => (
              <div
                key={l.purchaseRequestItemId}
                className="grid grid-cols-12 items-center gap-2 rounded-md border border-border/70 bg-card p-2"
              >
                <div className="col-span-7 min-w-0">
                  <div className="font-mono text-[11px] text-muted-foreground">{l.sku}</div>
                  <div className="truncate text-[12.5px] text-foreground">{l.name}</div>
                </div>
                <div className="col-span-3 text-right text-[11px] text-muted-foreground">
                  {l.remaining > 0 ? `${fmt.number(l.remaining)} pending` : 'fully received'}
                </div>
                <div className="col-span-2">
                  <Input
                    type="number"
                    inputMode="decimal"
                    min={0}
                    step="0.001"
                    value={l.qty}
                    onChange={(e) =>
                      setLines((ls) =>
                        ls.map((x, i) => (i === idx ? { ...x, qty: e.target.value } : x)),
                      )
                    }
                    className="h-8 text-right text-[12.5px] tabular-nums"
                  />
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="space-y-3">
          <SectionHeading title="Document references" />
          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-4">
              <FormField label="Invoice no." htmlFor="inv">
                <Input id="inv" value={invoiceNumber} onChange={(e) => setInvoiceNumber(e.target.value)} className="h-9" />
              </FormField>
            </div>
            <div className="col-span-3">
              <FormField label="Invoice date" htmlFor="invd">
                <Input id="invd" type="date" value={invoiceDate} onChange={(e) => setInvoiceDate(e.target.value)} className="h-9" />
              </FormField>
            </div>
            <div className="col-span-5">
              <FormField label="Amount paid" htmlFor="amt" hint="Open-market reconciliation.">
                <Input
                  id="amt"
                  type="number"
                  inputMode="decimal"
                  min={0}
                  step="0.01"
                  value={actualTotal}
                  onChange={(e) => setActualTotal(e.target.value)}
                  placeholder="₹"
                  className="h-9 text-right tabular-nums"
                />
              </FormField>
            </div>
            <div className="col-span-6">
              <FormField label="Challan no." htmlFor="chl">
                <Input id="chl" value={challanNumber} onChange={(e) => setChallanNumber(e.target.value)} className="h-9" />
              </FormField>
            </div>
            <div className="col-span-6">
              <FormField label="E-way bill no." htmlFor="eway">
                <Input id="eway" value={ewayBillNumber} onChange={(e) => setEwayBillNumber(e.target.value)} className="h-9" />
              </FormField>
            </div>
          </div>
        </section>

        <section className="space-y-3">
          <SectionHeading title="Attach documents" hint="PDF, PNG or JPEG · up to 10 MB each" />
          <div className="grid grid-cols-3 gap-3">
            {DOC_SLOTS.map((slot) => {
              const file = files[slot.kind];
              return (
                <label
                  key={slot.kind}
                  className="flex cursor-pointer flex-col gap-1.5 rounded-md border border-dashed border-border bg-card px-3 py-3 text-[12px] hover:border-primary/40"
                >
                  <span className="flex items-center gap-1.5 font-medium text-foreground">
                    <Paperclip className="h-3.5 w-3.5 text-muted-foreground" />
                    {slot.label}
                  </span>
                  {file ? (
                    <span className="flex items-center justify-between gap-1 text-[11px] text-muted-foreground">
                      <span className="truncate">{file.name}</span>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.preventDefault();
                          setFiles((f) => ({ ...f, [slot.kind]: undefined }));
                        }}
                        className="shrink-0 text-muted-foreground hover:text-destructive"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  ) : (
                    <span className="text-[11px] text-muted-foreground">Tap to choose</span>
                  )}
                  <input
                    type="file"
                    accept={ACCEPT}
                    className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) setFiles((prev) => ({ ...prev, [slot.kind]: f }));
                    }}
                  />
                </label>
              );
            })}
          </div>
        </section>

        <FormField label="Notes" htmlFor="rcptNotes">
          <Textarea
            id="rcptNotes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Condition on arrival, short quantities, QC remarks…"
            rows={2}
          />
        </FormField>
      </div>
    </FormSheet>
  );
}
